import { useState, useEffect, useCallback, useRef } from 'react';
import axios from 'axios';
import {
  Send, Eye, Users, Loader2, Check, Plus, Trash2,
  Mail, RefreshCw, Inbox, Search as SearchIcon, ChevronDown, X,
  CalendarClock, Ban, AlertTriangle,
} from 'lucide-react';

const API = '/api'; // Same-origin: /api routes on the serving domain (Preview or Production custom domain) to backend via Emergent ingress. Avoids cross-domain CORS / tracking-prevention failures.

function ToastNotif({ message, type = 'success' }) {
  if (!message) return null;
  const color = type === 'error' ? 'border-red-500/30 text-red-400 bg-red-500/10' : 'border-hg-green/30 text-hg-green bg-hg-surface';
  return (
    <div className={`fixed top-4 right-4 z-50 border rounded-sm px-4 py-3 text-sm font-sans flex items-center gap-2 animate-fade-in ${color}`}>
      <Check size={14} /> {message}
    </div>
  );
}

function AudiencePickerModal({ campaignId, currentAudience, onClose, onPicked }) {
  const [options, setOptions] = useState(null);
  const [tab, setTab] = useState('mailerlite_groups');
  const [search, setSearch] = useState('');
  const [picking, setPicking] = useState('');

  useEffect(() => {
    axios.get(`${API}/hub-campaign-audience-options`)
      .then((res) => setOptions(res.data))
      .catch(() => setOptions({ audiences: [], tags: [], mailerlite_groups: [] }));
  }, []);

  const handlePick = async (filter_type, filter_value) => {
    setPicking(`${filter_type}:${filter_value}`);
    try {
      await axios.put(`${API}/hub-campaigns/${campaignId}/audience`, { filter_type, filter_value });
      onPicked();
      onClose();
    } catch (e) {
      alert(e.response?.data?.detail || 'Failed to set audience');
    }
    setPicking('');
  };

  const tabConfig = {
    mailerlite_groups: { items: options?.mailerlite_groups, filterType: 'mailerlite_group', valueKey: 'id', label: 'Mailerlite Groups' },
    tags: { items: options?.tags, filterType: 'mailchimp_tag', valueKey: 'name', label: 'Mailchimp Tags' },
    audiences: { items: options?.audiences, filterType: 'mailchimp_audience', valueKey: 'name', label: 'Mailchimp Audiences' },
  };
  const cfg = tabConfig[tab];
  const items = cfg.items || [];
  const filtered = items.filter((i) => i.name.toLowerCase().includes(search.toLowerCase()));

  return (
    <div className="fixed inset-0 bg-black/40 z-50 flex items-center justify-center p-4" onClick={onClose}>
      <div className="bg-white border border-hg-border rounded-sm w-full max-w-lg max-h-[80vh] flex flex-col" onClick={(e) => e.stopPropagation()} data-testid="audience-picker-modal">
        <div className="flex items-center justify-between p-5 border-b border-hg-border">
          <h3 className="font-display text-lg text-hg-text tracking-wider">CHOOSE AUDIENCE</h3>
          <button onClick={onClose} className="text-hg-text-dim hover:text-hg-text"><X size={18} /></button>
        </div>
        <div className="px-5 pt-3 flex gap-1 border-b border-hg-border overflow-x-auto">
          {[
            { id: 'mailerlite_groups', label: 'Live Groups' },
            { id: 'tags', label: 'Mailchimp Tags' },
            { id: 'audiences', label: 'Mailchimp Audiences' },
          ].map((t) => (
            <button
              key={t.id}
              data-testid={`audience-tab-${t.id}`}
              onClick={() => setTab(t.id)}
              className={`px-3 py-2 text-xs font-sans uppercase tracking-[0.18em] border-b-2 transition-colors whitespace-nowrap ${
                tab === t.id ? 'border-hg-green text-hg-green' : 'border-transparent text-hg-text-dim hover:text-hg-text'
              }`}
            >{t.label}</button>
          ))}
        </div>
        <div className="p-4 border-b border-hg-border">
          <div className="relative">
            <SearchIcon size={12} className="absolute left-3 top-1/2 -translate-y-1/2 text-hg-text-dim" />
            <input
              data-testid="audience-search"
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              placeholder={`Search…`}
              className="w-full bg-white border border-hg-border rounded-sm pl-8 pr-3 py-2 text-xs focus:outline-none focus:ring-1 focus:ring-hg-green"
            />
          </div>
        </div>
        <div className="flex-1 overflow-y-auto p-2">
          {!options ? (
            <div className="flex justify-center py-12"><Loader2 size={16} className="animate-spin text-hg-green" /></div>
          ) : filtered.length === 0 ? (
            <div className="text-center py-8 text-xs text-hg-text-dim font-sans">No items match.</div>
          ) : (
            filtered.map((it) => {
              const value = it[cfg.valueKey];
              const isCurrent = currentAudience && currentAudience.filter_type === cfg.filterType && (currentAudience.filter_value === value || currentAudience.mailerlite_group_id === value);
              const isPicking = picking === `${cfg.filterType}:${value}`;
              return (
                <button
                  key={value}
                  data-testid={`audience-pick-${String(value).replace(/[^a-z0-9]/gi,'_').toLowerCase()}`}
                  onClick={() => handlePick(cfg.filterType, value)}
                  disabled={isPicking || isCurrent}
                  className={`w-full flex items-center justify-between px-3 py-2 text-left text-[12px] font-sans rounded-sm transition-colors mb-1 ${
                    isCurrent ? 'bg-hg-green/10 text-hg-green border border-hg-green/30' : 'text-hg-text hover:bg-hg-surface-hover border border-transparent'
                  } disabled:opacity-60`}
                >
                  <span className="truncate flex items-center gap-2">
                    {isCurrent && <Check size={11} />}
                    {it.name}
                  </span>
                  <span className="text-[10px] text-hg-text-dim flex-shrink-0">
                    {isPicking ? 'Working…' : `${it.count.toLocaleString()} active`}
                  </span>
                </button>
              );
            })
          )}
        </div>
        <div className="p-4 border-t border-hg-border text-[10px] font-sans text-hg-text-dim">
          {tab === 'mailerlite_groups'
            ? 'Live Mailerlite groups — including any curated lists you finalized. Pick to send directly.'
            : 'Picking a Mailchimp filter creates/refreshes a Mailerlite group with those subscribers. May take a few seconds.'}
        </div>
      </div>
    </div>
  );
}

function RecipientPanel({ campaignId, audience, onChangeAudience }) {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [page, setPage] = useState(1);
  const [search, setSearch] = useState('');
  const [expanded, setExpanded] = useState(false);

  const load = useCallback(() => {
    setLoading(true);
    const limit = expanded ? 50 : 10;
    axios.get(`${API}/hub-campaigns/${campaignId}/recipients`, { params: { limit, page, search } })
      .then((res) => setData(res.data))
      .catch(() => setData(null))
      .finally(() => setLoading(false));
  }, [campaignId, page, search, expanded]);

  useEffect(() => { load(); }, [load, audience?.filter_value]);

  const total = data?.total || 0;
  const totalPages = Math.max(1, Math.ceil(total / (expanded ? 50 : 10)));

  return (
    <div className="bg-hg-surface border border-hg-border rounded-sm p-4 space-y-3" data-testid="recipient-panel">
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-2 text-[10px] font-sans text-hg-text-dim uppercase tracking-[0.2em]">
          <Users size={11} strokeWidth={1.5} /> Recipients
        </div>
        <span className="text-[10px] font-display text-hg-green tracking-wider">{total.toLocaleString()}</span>
      </div>

      {audience ? (
        <div className="bg-hg-bg border border-hg-border rounded-sm p-2.5 space-y-1">
          <div className="flex items-center justify-between gap-2">
            <div className="min-w-0 flex-1">
              <div className="text-[11px] font-sans text-hg-text truncate">{audience.label}</div>
              <div className="text-[9px] text-hg-text-dim font-sans">
                {audience.filter_type === 'mailchimp_tag' && `Tag: ${audience.filter_value}`}
                {audience.filter_type === 'mailchimp_audience' && `Audience: ${audience.filter_value}`}
              </div>
            </div>
            <button
              data-testid="change-audience-btn"
              onClick={onChangeAudience}
              className="text-[10px] font-sans text-hg-green hover:underline whitespace-nowrap"
            >Change</button>
          </div>
        </div>
      ) : (
        <button
          data-testid="pick-audience-btn"
          onClick={onChangeAudience}
          className="w-full text-[11px] font-sans text-hg-green border border-hg-green/30 bg-hg-green/5 rounded-sm py-2 hover:bg-hg-green/10"
        >Choose an audience</button>
      )}

      <div className="relative">
        <SearchIcon size={11} className="absolute left-2.5 top-1/2 -translate-y-1/2 text-hg-text-dim" />
        <input
          data-testid="recipient-search"
          value={search}
          onChange={(e) => { setSearch(e.target.value); setPage(1); }}
          placeholder="Search recipients…"
          className="w-full bg-hg-bg border border-hg-border rounded-sm pl-7 pr-2 py-1.5 text-[11px] text-hg-text font-sans focus:outline-none focus:ring-1 focus:ring-hg-green"
        />
      </div>

      {loading ? (
        <div className="flex justify-center py-4"><Loader2 size={14} className="animate-spin text-hg-green" /></div>
      ) : data && data.items && data.items.length > 0 ? (
        <>
          <div className={`overflow-y-auto space-y-1 ${expanded ? 'max-h-[420px]' : 'max-h-60'}`} data-testid="recipient-list">
            {data.items.map((r) => (
              <div key={r.email} className="text-[11px] font-sans border-b border-hg-border/30 pb-1.5 last:border-b-0">
                <div className="text-hg-text truncate">{r.email}</div>
                {r.full_name && <div className="text-hg-text-dim text-[10px]">{r.full_name}</div>}
              </div>
            ))}
          </div>
          {total > data.items.length && (
            <div className="flex items-center justify-between text-[10px] font-sans pt-1">
              <button
                onClick={() => setExpanded(!expanded)}
                className="text-hg-green hover:underline flex items-center gap-1"
                data-testid="recipient-expand"
              >
                {expanded ? 'Compact view' : 'Show more'}
                <ChevronDown size={10} className={expanded ? 'rotate-180 transition-transform' : 'transition-transform'} />
              </button>
              {totalPages > 1 && (
                <div className="flex items-center gap-2">
                  <button
                    onClick={() => setPage((p) => Math.max(1, p - 1))}
                    disabled={page <= 1}
                    className="text-hg-text-dim hover:text-hg-text disabled:opacity-30"
                  >Prev</button>
                  <span className="text-hg-text-dim">{page}/{totalPages}</span>
                  <button
                    onClick={() => setPage((p) => Math.min(totalPages, p + 1))}
                    disabled={page >= totalPages}
                    className="text-hg-text-dim hover:text-hg-text disabled:opacity-30"
                  >Next</button>
                </div>
              )}
            </div>
          )}
        </>
      ) : (
        <div className="text-[11px] text-hg-text-dim font-sans py-3 flex items-center gap-1.5"><Inbox size={12} /> No recipients found.</div>
      )}
    </div>
  );
}

function ConfirmSendModal({ campaign, onClose, onConfirm }) {
  const [confirming, setConfirming] = useState(false);
  const [recipientCount, setRecipientCount] = useState(null);
  const excludeCount = (campaign.exclude_emails || []).length;
  const netCount = recipientCount !== null ? Math.max(0, recipientCount - excludeCount) : null;

  useEffect(() => {
    axios.get(`${API}/hub-campaigns/${campaign.id}/recipients?limit=1`)
      .then((res) => setRecipientCount(res.data.total || 0))
      .catch(() => setRecipientCount(null));
  }, [campaign.id]);

  const handle = async () => {
    setConfirming(true);
    await onConfirm();
    setConfirming(false);
  };

  return (
    <div className="fixed inset-0 bg-black/40 z-50 flex items-center justify-center p-4" onClick={onClose}>
      <div className="bg-white border border-hg-border rounded-sm w-full max-w-md p-6 space-y-4" onClick={(e) => e.stopPropagation()} data-testid="confirm-send-modal">
        <h3 className="font-display text-lg text-hg-text tracking-wider">CONFIRM SEND</h3>
        <div className="space-y-2 text-sm font-sans">
          <p className="text-hg-text">Send <span className="text-hg-green font-medium">{campaign.subject || '(no subject)'}</span></p>
          <div className="bg-hg-surface-hover rounded-sm p-3 space-y-1">
            <div className="flex justify-between text-xs">
              <span className="text-hg-text-dim">Audience</span>
              <span className="text-hg-text">{campaign.audience?.label || 'none'}</span>
            </div>
            <div className="flex justify-between text-xs">
              <span className="text-hg-text-dim">In audience</span>
              <span className="text-hg-text">{recipientCount !== null ? recipientCount.toLocaleString() : '…'}</span>
            </div>
            {excludeCount > 0 && (
              <div className="flex justify-between text-xs">
                <span className="text-hg-text-dim">Excluded</span>
                <span className="text-red-600">−{excludeCount.toLocaleString()}</span>
              </div>
            )}
            <div className="border-t border-hg-border pt-1 mt-1 flex justify-between text-sm font-medium">
              <span className="text-hg-text">Will receive</span>
              <span className="text-hg-green">{netCount !== null ? netCount.toLocaleString() : '…'}</span>
            </div>
          </div>
          <p className="text-[11px] text-hg-text-dim italic mt-2">Delivery starts immediately and cannot be undone.</p>
        </div>
        <div className="flex gap-2 justify-end pt-2">
          <button onClick={onClose} className="px-4 py-2 text-xs font-sans text-hg-text-dim border border-hg-border rounded-sm hover:text-hg-text">Cancel</button>
          <button
            data-testid="confirm-send-btn"
            onClick={handle}
            disabled={confirming || !recipientCount}
            className="px-4 py-2 text-xs font-sans bg-hg-green text-hg-text rounded-sm hover:bg-hg-green-hover transition-colors disabled:opacity-40 flex items-center gap-1.5"
          >
            {confirming ? <Loader2 size={12} className="animate-spin" /> : <Send size={12} />}
            {confirming ? 'Sending…' : 'Send Now'}
          </button>
        </div>
      </div>
    </div>
  );
}

export default function CampaignManager({ onNavigate } = {}) {
  const [campaigns, setCampaigns] = useState([]);
  const [activeId, setActiveId] = useState(null);
  const [campaign, setCampaign] = useState(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [previewHtml, setPreviewHtml] = useState('');
  const [notif, setNotif] = useState({ msg: '', type: 'success' });
  const [showConfirm, setShowConfirm] = useState(false);
  const [showAudiencePicker, setShowAudiencePicker] = useState(false);
  const [testRecipients, setTestRecipients] = useState('randallhenryhg@gmail.com, Grant@henry-griffitts.com');
  const [sendingTest, setSendingTest] = useState(false);
  // --- scheduling state (mirrors the Weekly Fitter / Newsletter composer) ---
  const [showSchedule, setShowSchedule] = useState(false);
  const [sendAt, setSendAt] = useState('');            // datetime-local value
  const [scheduleToTest, setScheduleToTest] = useState(false);
  const [confirmSchedule, setConfirmSchedule] = useState(false);
  const [scheduling, setScheduling] = useState(false);
  const [cancelling, setCancelling] = useState(false);
  const previewTimeout = useRef(null);

  const showNotif = (msg, type = 'success') => {
    setNotif({ msg, type });
    setTimeout(() => setNotif({ msg: '', type: 'success' }), 4000);
  };

  const loadList = useCallback(async () => {
    try {
      const res = await axios.get(`${API}/hub-campaigns`);
      setCampaigns(res.data.campaigns || []);
      // Deliberately NO auto-select: landing here must never put "Send to Audience"
      // one click away. The picker below opens a campaign only on an explicit tap.
    } catch (e) {
      showNotif('Failed to load campaigns', 'error');
    }
    setLoading(false);
    // No dependency on activeId: it is left over from the auto-select this function
    // deliberately no longer does, and it made every campaign tap refetch the whole
    // list. The list still refreshes on mount and after each explicit mutation below.
  }, []);

  const loadCampaign = useCallback(async (id) => {
    try {
      const res = await axios.get(`${API}/hub-campaigns/${id}`);
      setCampaign(res.data);
    } catch (e) { /* */ }
  }, []);

  const refreshPreview = useCallback(async (id) => {
    try {
      const res = await axios.post(`${API}/hub-campaigns/${id}/preview`);
      setPreviewHtml(res.data.html || '');
    } catch (e) { /* */ }
  }, []);

  useEffect(() => { loadList(); }, [loadList]);
  useEffect(() => { if (activeId) { loadCampaign(activeId); refreshPreview(activeId); } }, [activeId, loadCampaign, refreshPreview]);

  // Debounced auto-save + preview refresh as user types.
  // Derive the saved fields up front so the effect depends on stable primitives /
  // serialized keys instead of the `campaign` object identity (which changes every
  // render). The two JSON.stringify keys are extracted to variables so the dep array
  // can be statically checked, and the arrays are reconstructed from those keys —
  // keeping the request payload identical to reading `campaign.*` directly.
  const hasCampaign = !!campaign;
  const subject = campaign?.subject;
  const intro = campaign?.intro;
  const heroImage = campaign?.hero_image;
  const issueLabel = campaign?.issue_label;
  const dateLabel = campaign?.date_label ?? '';
  const sectionsKey = JSON.stringify(campaign?.sections ?? []);
  const exclusionsKey = JSON.stringify(campaign?.exclude_emails ?? []);

  useEffect(() => {
    if (!hasCampaign || !activeId) return;
    if (previewTimeout.current) clearTimeout(previewTimeout.current);
    previewTimeout.current = setTimeout(async () => {
      try {
        setSaving(true);
        const res = await axios.put(`${API}/hub-campaigns/${activeId}`, {
          subject,
          intro,
          sections: JSON.parse(sectionsKey),
          hero_image: heroImage,
          issue_label: issueLabel,
          date_label: dateLabel,
        });
        await axios.put(`${API}/hub-campaigns/${activeId}/exclusions`, {
          exclude_emails: JSON.parse(exclusionsKey),
        });
        // A content edit may have invalidated a prior test or cancelled a queued
        // send server-side. Sync just the workflow fields back (never the content
        // fields, which the user may still be editing) so the UI reflects it.
        const d = res.data || {};
        setCampaign((c) => (c ? {
          ...c,
          status: d.status,
          test_sent_at: d.test_sent_at,
          scheduled_at: d.scheduled_at,
          scheduled_test: d.scheduled_test,
          scheduled_group_name: d.scheduled_group_name,
          mailerlite_campaign_id: d.mailerlite_campaign_id,
        } : c));
        await refreshPreview(activeId);
        setSaving(false);
      } catch (e) { setSaving(false); }
    }, 800);
    return () => previewTimeout.current && clearTimeout(previewTimeout.current);
  }, [hasCampaign, activeId, subject, intro, sectionsKey, heroImage, issueLabel, dateLabel, exclusionsKey, refreshPreview]);

  const updateField = (key, value) => {
    setCampaign((c) => ({ ...c, [key]: value }));
  };

  const updateSection = (i, key, value) => {
    setCampaign((c) => {
      const sections = [...c.sections];
      sections[i] = { ...sections[i], [key]: value };
      return { ...c, sections };
    });
  };

  const addSection = () => {
    setCampaign((c) => ({ ...c, sections: [...c.sections, { title: '', copy: '' }] }));
  };

  const removeSection = (i) => {
    setCampaign((c) => ({ ...c, sections: c.sections.filter((_, idx) => idx !== i) }));
  };

  // datetime-local -> ISO with the user's local offset, so the server's future-check
  // is correct and MailerLite gets the right wall-clock time. (Mirrors the WF composer.)
  const toOffsetISO = (local) => {
    const d = new Date(local);
    const pad = (n) => String(Math.abs(n)).padStart(2, '0');
    const tzMin = -d.getTimezoneOffset();
    const sign = tzMin >= 0 ? '+' : '-';
    return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}` +
      `T${pad(d.getHours())}:${pad(d.getMinutes())}:00` +
      `${sign}${pad(Math.trunc(Math.abs(tzMin) / 60))}:${pad(Math.abs(tzMin) % 60)}`;
  };

  const handleSendTest = async () => {
    setSendingTest(true);
    try {
      const emails = testRecipients.split(',').map((s) => s.trim()).filter(Boolean);
      if (emails.length === 0) {
        showNotif('Enter at least one recipient email', 'error');
        setSendingTest(false);
        return;
      }
      await axios.post(`${API}/hub-campaigns/${activeId}/send-test`, { recipient_emails: emails });
      // Reload so test_sent_at is reflected — that unlocks scheduling.
      await loadCampaign(activeId);
      showNotif(`Test sent to ${emails.length} recipient${emails.length === 1 ? '' : 's'}. Check inbox in 1–3 min.`);
    } catch (e) {
      showNotif(e.response?.data?.detail || 'Test send failed', 'error');
    }
    setSendingTest(false);
  };

  const handleSchedule = async () => {
    if (!sendAt) { showNotif('Pick a date & time', 'error'); return; }
    if (!confirmSchedule) { showNotif('Confirm the scheduled send first', 'error'); return; }
    if (new Date(sendAt) <= new Date()) { showNotif('Pick a time in the future', 'error'); return; }
    setScheduling(true);
    try {
      const res = await axios.post(`${API}/hub-campaigns/${activeId}/schedule`, {
        send_at: toOffsetISO(sendAt),
        confirm: true,
        test: scheduleToTest,
      });
      setShowSchedule(false);
      setConfirmSchedule(false);
      const wasTest = scheduleToTest;
      setScheduleToTest(false);
      await loadList();
      await loadCampaign(activeId);
      showNotif(wasTest
        ? 'Test schedule created — see it on the Content Calendar.'
        : `Scheduled for ${new Date(res.data.scheduled_at).toLocaleString()} — see it on the Content Calendar.`);
    } catch (e) {
      showNotif(e.response?.data?.detail || 'Schedule failed', 'error');
    }
    setScheduling(false);
  };

  const handleCancelSchedule = async () => {
    if (!window.confirm('Cancel the scheduled send and return this campaign to draft?')) return;
    setCancelling(true);
    try {
      await axios.post(`${API}/hub-campaigns/${activeId}/cancel-schedule`);
      await loadList();
      await loadCampaign(activeId);
      showNotif('Scheduled send cancelled — back to draft.');
    } catch (e) {
      showNotif(e.response?.data?.detail || 'Cancel failed', 'error');
    }
    setCancelling(false);
  };

  const handleSendReal = async () => {
    try {
      const res = await axios.post(`${API}/hub-campaigns/${activeId}/send`);
      showNotif('Campaign sent! Delivery in progress.');
      setShowConfirm(false);
      await loadList();
      await loadCampaign(activeId);
      if (res.data?.review_url) {
        window.open(res.data.review_url, '_blank', 'noopener');
      }
    } catch (e) {
      showNotif(e.response?.data?.detail || 'Send failed', 'error');
    }
  };

  if (loading) {
    return <div className="flex justify-center py-20"><Loader2 size={20} className="animate-spin text-hg-green" /></div>;
  }

  // Neutral landing: a campaign list. Send controls exist only inside an opened campaign.
  if (!activeId) {
    return (
      <div className="space-y-3" data-testid="campaign-picker">
        <p className="text-xs text-hg-text-dim font-sans">
          Select a campaign to open it. Nothing sends from this list.
        </p>
        {campaigns.length === 0 ? (
          <div className="text-center py-16 text-hg-text-dim font-sans text-sm">No campaigns yet.</div>
        ) : campaigns.map((c) => (
          <button
            key={c.id}
            data-testid={`campaign-pick-${c.id}`}
            onClick={() => setActiveId(c.id)}
            className="w-full text-left bg-hg-surface border border-hg-border rounded-sm px-4 py-3 hover:border-hg-green transition-colors flex items-center justify-between gap-3"
          >
            <div className="min-w-0">
              <div className="text-sm font-sans text-hg-text truncate">{c.name}</div>
              <div className="text-[10px] text-hg-text-dim font-sans mt-0.5">
                {c.updated_at ? new Date(c.updated_at).toLocaleString() : ''}
              </div>
            </div>
            <span className={`text-[10px] uppercase tracking-wider px-2 py-0.5 rounded-sm flex-shrink-0 ${
              c.status === 'sent' ? 'bg-hg-border text-hg-text-dim' :
              c.status === 'scheduled' ? 'bg-hg-green/10 text-hg-green' :
              'bg-yellow-500/10 text-yellow-400'
            }`}>{c.status}</span>
          </button>
        ))}
      </div>
    );
  }

  if (!campaign) {
    return <div className="flex justify-center py-20"><Loader2 size={20} className="animate-spin text-hg-green" /></div>;
  }

  const isSent = campaign.status === 'sent';
  const isScheduled = campaign.status === 'scheduled';
  const hasTested = !!campaign.test_sent_at;
  const fmtDate = (s) => { try { return new Date(s).toLocaleString(); } catch { return s; } };
  const mlUrl = campaign.mailerlite_campaign_id
    ? `https://dashboard.mailerlite.com/campaigns/${campaign.mailerlite_campaign_id}/preview` : null;
  const audienceLabel = campaign.audience?.label || 'your audience';

  return (
    <div className="space-y-5" data-testid="campaign-manager">
      <ToastNotif message={notif.msg} type={notif.type} />
      {showConfirm && <ConfirmSendModal campaign={campaign} onClose={() => setShowConfirm(false)} onConfirm={handleSendReal} />}
      {showAudiencePicker && (
        <AudiencePickerModal
          campaignId={activeId}
          currentAudience={campaign.audience}
          onClose={() => setShowAudiencePicker(false)}
          onPicked={() => { loadCampaign(activeId); showNotif('Audience updated'); }}
        />
      )}

      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-3">
        <div>
          <button
            data-testid="campaign-back-to-list"
            onClick={() => { setActiveId(null); setCampaign(null); setPreviewHtml(''); }}
            className="text-[11px] font-sans text-hg-text-dim hover:text-hg-green transition-colors mb-1"
          >← All campaigns</button>
          <h2 className="font-display text-xl text-hg-text tracking-wider">{campaign.name.toUpperCase()}</h2>
          <p className="text-xs text-hg-text-dim font-sans mt-1">
            Status: <span className={isSent ? 'text-hg-text-dim' : 'text-hg-green'}>{campaign.status.toUpperCase()}</span>
            {saving && <span className="ml-3 text-hg-text-dim italic">Saving…</span>}
          </p>
        </div>
        <div className="flex items-center gap-2">
          <button
            data-testid="campaign-send-test-btn"
            onClick={handleSendTest}
            disabled={sendingTest || isSent}
            className="flex items-center gap-1.5 px-3 py-1.5 text-xs font-sans border border-hg-border text-hg-text rounded-sm hover:border-hg-green hover:text-hg-green transition-colors disabled:opacity-40"
          >
            {sendingTest ? <Loader2 size={12} className="animate-spin" /> : <Mail size={12} />}
            Send Test
          </button>
          <button
            data-testid="campaign-schedule-btn"
            onClick={() => setShowSchedule((s) => !s)}
            disabled={isSent || isScheduled || !hasTested}
            title={!hasTested ? 'Send a test first' : isScheduled ? 'Already scheduled' : 'Schedule for later'}
            className="flex items-center gap-1.5 px-3 py-1.5 text-xs font-sans border border-hg-green text-hg-green rounded-sm hover:bg-hg-green/10 transition-colors disabled:opacity-40 disabled:cursor-not-allowed"
          >
            <CalendarClock size={12} /> Schedule…
          </button>
          <button
            data-testid="campaign-send-real-btn"
            onClick={() => setShowConfirm(true)}
            disabled={isSent || isScheduled}
            className="flex items-center gap-1.5 px-3 py-1.5 text-xs font-sans bg-hg-green text-hg-text rounded-sm hover:bg-hg-green-hover transition-colors disabled:opacity-40"
          >
            <Send size={12} /> {isSent ? 'Sent' : 'Send to Audience'}
          </button>
        </div>
      </div>

      {/* Test-first hint (mirrors the WF/Newsletter guardrail) */}
      {!hasTested && !isSent && !isScheduled && (
        <p className="text-[11px] text-hg-text-dim font-sans flex items-center gap-1.5">
          <AlertTriangle size={12} /> Send a test before you can schedule this campaign.
        </p>
      )}

      {/* Schedule panel — only after a test, only when not already scheduled/sent */}
      {showSchedule && hasTested && !isScheduled && !isSent && (
        <div className="border border-hg-green/50 rounded-sm p-4 space-y-3 bg-hg-green/5" data-testid="campaign-schedule-panel">
          <p className="text-xs font-sans text-hg-text">
            Schedule a send to <strong>{scheduleToTest ? 'HG Hub Test (just you)' : audienceLabel}</strong> at the time below.
            MailerLite fires it automatically at that moment — it stays visible and cancellable until then.
          </p>
          <div className="flex flex-wrap items-center gap-2">
            <input
              type="datetime-local"
              data-testid="campaign-schedule-datetime"
              value={sendAt}
              onChange={(e) => setSendAt(e.target.value)}
              className="bg-hg-bg border border-hg-border rounded-sm px-2 py-1.5 text-sm font-sans text-hg-text focus:border-hg-green focus:outline-none"
            />
          </div>
          <label className="flex items-start gap-2 text-[11px] font-sans text-hg-text-dim cursor-pointer">
            <input type="checkbox" checked={scheduleToTest} onChange={(e) => setScheduleToTest(e.target.checked)} className="mt-0.5" />
            <span>Dry-run: schedule to the <strong>HG Hub Test</strong> group (just you), not the real audience — for validating the scheduler.</span>
          </label>
          <label className="flex items-start gap-2 text-xs font-sans text-hg-text cursor-pointer">
            <input
              type="checkbox"
              data-testid="campaign-schedule-confirm"
              checked={confirmSchedule}
              onChange={(e) => setConfirmSchedule(e.target.checked)}
              className="mt-0.5"
            />
            <span>I confirm scheduling a send to <strong>{scheduleToTest ? 'HG Hub Test (just you)' : audienceLabel}</strong> at the time above.</span>
          </label>
          <div className="flex gap-2">
            <button
              data-testid="campaign-schedule-confirm-btn"
              onClick={handleSchedule}
              disabled={scheduling || !confirmSchedule || !sendAt}
              className="flex items-center gap-1.5 px-4 py-2 text-sm font-sans bg-hg-green text-hg-text rounded-sm hover:bg-hg-green-hover disabled:opacity-40 disabled:cursor-not-allowed"
            >
              {scheduling ? <Loader2 size={14} className="animate-spin" /> : <CalendarClock size={14} />}
              {scheduleToTest ? 'Confirm & schedule (test)' : 'Confirm & schedule'}
            </button>
            <button
              onClick={() => { setShowSchedule(false); setConfirmSchedule(false); }}
              className="px-4 py-2 text-sm font-sans border border-hg-border text-hg-text-dim rounded-sm hover:border-hg-green"
            >Cancel</button>
          </div>
        </div>
      )}

      {/* Scheduled state + cancel */}
      {isScheduled && (
        <div className="border-2 border-hg-green rounded-sm p-4 space-y-2 bg-hg-green/10" data-testid="campaign-scheduled-banner">
          <div className="flex items-center gap-2 text-sm font-sans font-semibold text-hg-text">
            <CalendarClock size={16} className="text-hg-green" /> Scheduled
            {campaign.scheduled_test && <span className="text-[10px] font-normal text-hg-text-dim">(test dry-run)</span>}
          </div>
          <p className="text-sm font-sans text-hg-text">
            Sending to <strong>{campaign.scheduled_group_name || (campaign.scheduled_test ? 'HG Hub Test (just you)' : audienceLabel)}</strong> on <strong>{fmtDate(campaign.scheduled_at)}</strong>.
          </p>
          <div className="flex flex-wrap gap-2 pt-1">
            {onNavigate && (
              <button
                onClick={() => onNavigate('calendar')}
                className="flex items-center gap-1.5 px-3 py-1.5 text-xs font-sans bg-hg-green text-hg-text rounded-sm hover:bg-hg-green-hover"
              >
                <CalendarClock size={13} /> View on Content Calendar
              </button>
            )}
            {mlUrl && (
              <a href={mlUrl} target="_blank" rel="noreferrer"
                className="flex items-center gap-1.5 px-3 py-1.5 text-xs font-sans border border-hg-green text-hg-green rounded-sm hover:bg-hg-green/10">
                <Eye size={13} /> View in MailerLite
              </a>
            )}
            <button
              data-testid="campaign-cancel-schedule-btn"
              onClick={handleCancelSchedule}
              disabled={cancelling}
              className="flex items-center gap-1.5 px-3 py-1.5 text-xs font-sans border border-red-400 text-red-400 rounded-sm hover:bg-red-400/10 disabled:opacity-40"
            >
              {cancelling ? <Loader2 size={13} className="animate-spin" /> : <Ban size={13} />} Cancel schedule
            </button>
          </div>
          <p className="text-[10px] text-hg-text-dim font-sans">
            Editing the content below will cancel this schedule and return the campaign to draft.
          </p>
        </div>
      )}

      <div className="grid grid-cols-1 lg:grid-cols-[1fr_440px] gap-4">
        {/* LEFT: Editor */}
        <div className="space-y-4">
          {/* Subject */}
          <div className="bg-hg-surface border border-hg-border rounded-sm p-4 space-y-2">
            <label className="text-[10px] font-sans text-hg-text-dim uppercase tracking-[0.2em]">Subject Line</label>
            <input
              data-testid="campaign-subject"
              type="text"
              value={campaign.subject || ''}
              onChange={(e) => updateField('subject', e.target.value)}
              disabled={isSent}
              className="w-full bg-hg-bg border border-hg-border rounded-sm px-3 py-2 text-sm text-hg-text font-sans focus:outline-none focus:border-hg-green/50 disabled:opacity-60"
            />
          </div>

          {/* Issue / Date label (optional) */}
          <div className="bg-hg-surface border border-hg-border rounded-sm p-4 space-y-2">
            <label className="text-[10px] font-sans text-hg-text-dim uppercase tracking-[0.2em]">Date Line (optional)</label>
            <input
              data-testid="campaign-date-label"
              type="text"
              value={campaign.date_label || ''}
              onChange={(e) => updateField('date_label', e.target.value)}
              disabled={isSent}
              placeholder="Leave blank to hide — or type e.g. 'June 2026 · Issue 12'"
              className="w-full bg-hg-bg border border-hg-border rounded-sm px-3 py-2 text-sm text-hg-text font-sans focus:outline-none focus:border-hg-green/50 disabled:opacity-60"
            />
            <p className="text-[10px] text-hg-text-dim font-sans">Shows under the subject in small caps. Blank = no date line.</p>
          </div>

          {/* Intro */}
          <div className="bg-hg-surface border border-hg-border rounded-sm p-4 space-y-2">
            <label className="text-[10px] font-sans text-hg-text-dim uppercase tracking-[0.2em]">Opening Paragraph</label>
            <textarea
              data-testid="campaign-intro"
              value={campaign.intro || ''}
              onChange={(e) => updateField('intro', e.target.value)}
              disabled={isSent}
              rows={3}
              className="w-full bg-hg-bg border border-hg-border rounded-sm px-3 py-2 text-sm text-hg-text font-sans focus:outline-none focus:border-hg-green/50 disabled:opacity-60 resize-none"
            />
          </div>

          {/* Sections */}
          <div className="space-y-3">
            <div className="flex items-center justify-between">
              <label className="text-[10px] font-sans text-hg-text-dim uppercase tracking-[0.2em]">Sections</label>
              {!isSent && (
                <button
                  data-testid="campaign-add-section"
                  onClick={addSection}
                  className="flex items-center gap-1 text-[10px] text-hg-green font-sans hover:underline"
                >
                  <Plus size={10} /> Add Section
                </button>
              )}
            </div>
            {(campaign.sections || []).map((s, i) => (
              <div key={i} className="bg-hg-surface border border-hg-border rounded-sm p-4 space-y-2" data-testid={`campaign-section-${i}`}>
                <div className="flex items-center justify-between">
                  <span className="text-[10px] font-sans text-hg-text-dim">Section {i + 1}</span>
                  {!isSent && (
                    <button
                      data-testid={`campaign-remove-section-${i}`}
                      onClick={() => removeSection(i)}
                      className="text-hg-text-dim hover:text-red-400"
                    ><Trash2 size={11} /></button>
                  )}
                </div>
                <input
                  type="text"
                  value={s.title || ''}
                  placeholder="Section heading"
                  onChange={(e) => updateSection(i, 'title', e.target.value)}
                  disabled={isSent}
                  className="w-full bg-hg-bg border border-hg-border rounded-sm px-3 py-2 text-sm text-hg-text font-sans focus:outline-none focus:border-hg-green/50 disabled:opacity-60"
                />
                <textarea
                  value={s.copy || ''}
                  placeholder="Section body — use bullet markers (•) for lists, blank line for paragraph break"
                  onChange={(e) => updateSection(i, 'copy', e.target.value)}
                  disabled={isSent}
                  rows={6}
                  className="w-full bg-hg-bg border border-hg-border rounded-sm px-3 py-2 text-sm text-hg-text font-sans focus:outline-none focus:border-hg-green/50 disabled:opacity-60 resize-y"
                />
              </div>
            ))}
          </div>

          {/* Exclusion list */}
          <div className="bg-white border border-hg-border rounded-sm p-4 space-y-2">
            <label className="text-[10px] font-sans text-hg-text-dim uppercase tracking-[0.2em]">Exclude These Emails ({(campaign.exclude_emails || []).length})</label>
            <textarea
              data-testid="campaign-exclude-emails"
              value={(campaign.exclude_emails || []).join('\n')}
              onChange={(e) => updateField('exclude_emails', e.target.value.split(/[\n,]+/).map((s) => s.trim()).filter(Boolean))}
              disabled={isSent}
              rows={4}
              placeholder="One email per line. These addresses will be skipped for this send only."
              className="w-full bg-white border border-hg-border rounded-sm px-3 py-2 text-xs font-mono focus:outline-none focus:border-hg-green/50 disabled:opacity-60 resize-y"
            />
            <p className="text-[10px] text-hg-text-dim font-sans">One per line or comma-separated. Excluded just for this campaign — the subscribers stay in your group.</p>
          </div>

          {/* Test recipients */}
          {!isSent && (
            <div className="bg-hg-surface border border-hg-border rounded-sm p-4 space-y-2">
              <label className="text-[10px] font-sans text-hg-text-dim uppercase tracking-[0.2em]">Test Recipients</label>
              <input
                data-testid="campaign-test-recipients"
                type="text"
                value={testRecipients}
                onChange={(e) => setTestRecipients(e.target.value)}
                placeholder="email1@x.com, email2@y.com"
                className="w-full bg-hg-bg border border-hg-border rounded-sm px-3 py-2 text-xs text-hg-text font-sans focus:outline-none focus:border-hg-green/50"
              />
              <p className="text-[10px] text-hg-text-dim font-sans">Comma-separated. Used by the Send Test button above.</p>
            </div>
          )}
        </div>

        {/* RIGHT: Recipients + Preview */}
        <div className="space-y-4">
          <RecipientPanel campaignId={campaign.id} audience={campaign.audience} onChangeAudience={() => setShowAudiencePicker(true)} />
          <div className="bg-hg-surface border border-hg-border rounded-sm overflow-hidden" data-testid="campaign-preview">
            <div className="px-4 py-2 border-b border-hg-border flex items-center justify-between">
              <div className="flex items-center gap-2 text-[10px] font-sans text-hg-text-dim uppercase tracking-[0.2em]">
                <Eye size={11} strokeWidth={1.5} /> Live Preview
              </div>
              <button
                onClick={() => refreshPreview(activeId)}
                className="text-hg-text-dim hover:text-hg-green"
                data-testid="campaign-preview-refresh"
              ><RefreshCw size={11} /></button>
            </div>
            {previewHtml ? (
              <iframe
                srcDoc={previewHtml}
                title="Live preview"
                className="w-full border-0 bg-white"
                style={{ height: '700px' }}
                sandbox="allow-same-origin"
              />
            ) : (
              <div className="flex items-center justify-center h-72"><Loader2 size={16} className="animate-spin text-hg-green" /></div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}
